import { useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { verifyEmail } from '@/api/auth'
import type { ApiError } from '@/api/client'

/**
 * Email-confirmation landing page. Reads {@code ?token=...} from the verification link and
 * confirms it against the backend. Only reachable when the backend has verification enabled;
 * with it disabled no link is ever sent, so this page is simply never visited.
 */
export default function Verify() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const verify = useMutation({ mutationFn: (t: string) => verifyEmail(t) })

  // Fire the confirmation once on mount (mutate is an async action, not a render-time setState).
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) return
    fired.current = true
    if (token) verify.mutate(token)
  }, [token, verify])

  const error = verify.error as ApiError | null

  let body
  if (!token) {
    body = <p className="text-sm text-danger">This verification link is missing its token.</p>
  } else if (verify.isSuccess) {
    body = (
      <>
        <p className="text-sm text-gray-500 mb-6">
          Your email is confirmed. You can sign in now.
        </p>
        <Link
          to="/login"
          className="inline-block w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
        >
          Go to sign in
        </Link>
      </>
    )
  } else if (error) {
    body = (
      <>
        <p className="text-sm text-danger mb-6">{error.message}</p>
        <Link
          to="/register"
          className="inline-block w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
        >
          Back to sign up
        </Link>
      </>
    )
  } else {
    body = <p className="text-sm text-gray-500">Confirming your email…</p>
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm text-center">
        <img src="/brand/profitpipe-wordmark.svg" alt="ProfitPipe" className="h-5 mx-auto mb-5" />
        <h1 className="text-2xl font-bold text-brand-darkest mb-4">Email verification</h1>
        {body}
      </div>
    </div>
  )
}
